import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm, FieldPath } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import FormStepper from '@/components/ui/FormStepper/FormStepper';
import { StepDefinition } from '@/components/ui/FormStepper/types';
import FundRulesDialog from './components/FundRulesDialog';
import OpenFundEarlyLeadCapture, { OPEN_FUND_EARLY_LEAD_PANEL_ID } from './components/OpenFundEarlyLeadCapture';
import { FundClassification } from '@/models/common/FundClassification';
import type { OpenFundRequestDtoOut } from '@/models/out/OpenFundRequestDtoOut';
import type { OpenFundEarlyLeadDtoOut } from '@/models/out/OpenFundEarlyLeadDtoOut';
import type { OpenFundEarlyLeadDtoIn } from '@/models/in/OpenFundEarlyLeadDtoIn';
import { usePostApi, usePutApi } from '@/hooks/UseApiCalls';
import { offerTaskSatisfaction } from '@/helpers/surveyStorage';
import { ANALYTICS_EVENTS } from '@/helpers/analyticsEvents';
import {
  FUND_FORM_UNKNOWN_FIELD,
  OPEN_FUND_ANALYTICS_STEPS,
  OpenFundAnalyticsStepId,
  buildOpenFundAnalyticsProps,
  countErrorFields,
  firstErrorField,
  fundTypeFromClassification,
  openFundStepFromFieldName,
  shouldAbandonOnUnmount,
  shouldCaptureFundFormAbandoned,
} from '@/helpers/fundFormAnalytics';
import { isPostHogStarted } from '@/helpers/posthogClient';
import { usePostHogIdentification } from '@/hooks/usePostHogIdentification';
import { OpenFundAnalyticsProvider } from './OpenFundAnalyticsContext';
import { clearStoredOpenFundEarlyLead, readStoredOpenFundEarlyLead, writeStoredOpenFundEarlyLead } from './openFundEarlyLeadStorage';
import { parseOpenFundFormLeadSnapshot, serializeOpenFundFormForLead } from './utils/openFundFormDraft';

import { getDefaultValues, openFundFormSchema, openFundFormEditSchema, OpenFundFormData } from './schemas/openFundFormSchema';
import FundDefinitionStep from './steps/FundDefinitionStep';
import SupportedsStep from './steps/SupportedsStep';
import ApplicantAndCoordinatorsStep from './steps/ApplicantAndCoordinatorsStep';
import TermsAndSignatureStep from './steps/TermsAndSignatureStep';
import { mapFormDataToDto } from './utils/mapFormDataToDto';
import { mapFormDataToResubmitDto } from './utils/mapFormDataToResubmitDto';
import { extractErrorMessage } from './utils/extractErrorMessage';
import { EditModeContext } from './EditModeContext';
import { ExistingDocumentsContext, ExistingDocumentsValue } from './ExistingDocumentsContext';
import { scrollToFirstInvalidField } from '@/helpers/formValidationUi';
import { translateFileReadError, EmptyFileError, FileReadError } from '@/helpers/fileReaderHelper';
import formLayoutStyles from './OpenFundForm.module.scss';

const EMPTY_EXISTING_DOCS: ExistingDocumentsValue = { single: {}, additional: {} };

interface OpenFundFormProps {
  classification: FundClassification.Welfare | FundClassification.Community;
  onSuccess: () => void;
  editRequestId?: number;
  editToken?: string;
  initialValues?: OpenFundFormData;
  existingDocuments?: ExistingDocumentsValue;
  privacyAcceptedAt?: string | null;
  /** Restored anonymous early-lead draft (not office incomplete flow). */
  resumedEarlyLead?: OpenFundEarlyLeadDtoOut | null;
}

const OpenFundForm = ({
  classification,
  onSuccess,
  editRequestId,
  editToken,
  initialValues,
  existingDocuments,
  privacyAcceptedAt,
  resumedEarlyLead,
}: OpenFundFormProps) => {
  const { t } = useTranslation();
  const { captureEvent, posthog } = usePostHogIdentification();
  const isEditMode = editRequestId != null && !!editToken;
  const [showRulesDialog, setShowRulesDialog] = useState(false);
  const [rulesAccepted, setRulesAccepted] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [earlyLeadContactSaved, setEarlyLeadContactSaved] = useState(!!resumedEarlyLead);
  const [earlyLeadSuccessFlash, setEarlyLeadSuccessFlash] = useState(false);
  const fundType = fundTypeFromClassification(classification);
  const lastFieldRef = useRef<string>(FUND_FORM_UNKNOWN_FIELD);
  const submittedOkRef = useRef(false);
  const abandonedRef = useRef(false);
  const currentStepRef = useRef<OpenFundAnalyticsStepId>(OPEN_FUND_ANALYTICS_STEPS.FUND_DEFINITION);
  const formStepIndexRef = useRef(0);
  const leadIdRef = useRef<string | null>(resumedEarlyLead?.leadId ?? readStoredOpenFundEarlyLead()?.leadId ?? null);
  const leadTokenRef = useRef<string | null>(resumedEarlyLead?.resumeToken ?? readStoredOpenFundEarlyLead()?.resumeToken ?? null);
  const leadSaveInFlightRef = useRef(false);
  const [earlyLeadExpanded, setEarlyLeadExpanded] = useState(false);
  const earlyLeadEmailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!earlyLeadExpanded) return;
    requestAnimationFrame(() => {
      earlyLeadEmailRef.current?.focus();
    });
  }, [earlyLeadExpanded]);

  const trackFieldInteraction = useCallback(
    (fieldName: string) => {
      if (fieldName.trim()) {
        lastFieldRef.current = fieldName;
        currentStepRef.current = openFundStepFromFieldName(fieldName, classification === FundClassification.Welfare);
      }
    },
    [classification]
  );

  const mergedInitialValues = useMemo(() => {
    const base = initialValues ?? getDefaultValues(classification);
    if (!resumedEarlyLead?.formStateJson) return base;
    const snapshot = parseOpenFundFormLeadSnapshot(resumedEarlyLead.formStateJson);
    return snapshot ? ({ ...base, ...snapshot } as OpenFundFormData) : base;
  }, [classification, initialValues, resumedEarlyLead]);

  const defaultValues = useMemo(() => mergedInitialValues, [mergedInitialValues]);

  const methods = useForm<OpenFundFormData>({
    resolver: zodResolver(isEditMode ? openFundFormEditSchema : openFundFormSchema) as never,
    defaultValues: defaultValues as never,
    mode: 'onChange',
  });

  useEffect(() => {
    if (resumedEarlyLead?.formStateJson) {
      methods.reset(defaultValues as never);
    }
  }, [defaultValues, methods, resumedEarlyLead]);

  const { mutateAsync: submitRequest, isPending } = usePostApi<unknown, OpenFundRequestDtoOut>('funds/creation/open-fund');

  const { mutateAsync: resubmitRequest, isPending: isResubmitPending } = usePutApi<unknown, OpenFundRequestDtoOut>(
    isEditMode ? `funds/creation/${editRequestId}/resubmit?token=${encodeURIComponent(editToken!)}` : ''
  );

  const { mutateAsync: upsertEarlyLead, isPending: isLeadSaving } = usePostApi<OpenFundEarlyLeadDtoOut, OpenFundEarlyLeadDtoIn>(
    'funds/creation/open-fund-early-lead'
  );

  const isWelfare = classification === FundClassification.Welfare;

  const analyticsExtras = useCallback(
    () => ({
      last_field: lastFieldRef.current,
      lead_id: leadIdRef.current ?? undefined,
    }),
    []
  );

  const captureInstant = useCallback(
    (eventName: string, props: Record<string, string>) => {
      if (!isPostHogStarted()) return;
      if (eventName === ANALYTICS_EVENTS.FUND_FORM_ABANDONED && posthog) {
        posthog.capture(eventName, props, { transport: 'sendBeacon' });
        return;
      }
      captureEvent(eventName, props);
    },
    [captureEvent, posthog]
  );

  useEffect(() => {
    if (resumedEarlyLead) {
      formStepIndexRef.current = resumedEarlyLead.formStepIndex ?? 0;
      if (resumedEarlyLead.analyticsStep) {
        currentStepRef.current = resumedEarlyLead.analyticsStep as OpenFundAnalyticsStepId;
      }
      writeStoredOpenFundEarlyLead({
        leadId: resumedEarlyLead.leadId,
        resumeToken: resumedEarlyLead.resumeToken,
      });
    }
  }, [resumedEarlyLead]);

  useEffect(() => {
    captureEvent(ANALYTICS_EVENTS.FUND_FORM_STARTED, buildOpenFundAnalyticsProps(fundType, currentStepRef.current, analyticsExtras()));
    const startedAt = Date.now();
    const abandon = () => {
      if (!shouldCaptureFundFormAbandoned({ submittedOk: submittedOkRef.current, abandoned: abandonedRef.current })) {
        return;
      }
      abandonedRef.current = true;
      captureInstant(ANALYTICS_EVENTS.FUND_FORM_ABANDONED, buildOpenFundAnalyticsProps(fundType, currentStepRef.current, analyticsExtras()));
    };
    const handlePageHide = () => abandon();
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      if (!shouldAbandonOnUnmount(startedAt, Date.now())) return;
      abandon();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fundType]);

  const persistEarlyLead = useCallback(
    async (contact?: { email: string; phone: string }) => {
      if (isEditMode) return;
      const body: OpenFundEarlyLeadDtoIn = {
        leadId: leadIdRef.current,
        resumeToken: leadTokenRef.current,
        fundClassification: classification,
        email: contact?.email || undefined,
        phone: contact?.phone || undefined,
        formStateJson: serializeOpenFundFormForLead(methods.getValues()),
        formStepIndex: formStepIndexRef.current,
        analyticsStep: currentStepRef.current,
      };
      const result = await upsertEarlyLead(body);
      leadIdRef.current = result.leadId;
      leadTokenRef.current = result.resumeToken;
      writeStoredOpenFundEarlyLead({ leadId: result.leadId, resumeToken: result.resumeToken });
      return result;
    },
    [classification, isEditMode, methods, upsertEarlyLead]
  );

  const handleLeadCapture = async (contact: { email: string; phone: string }): Promise<boolean> => {
    if (leadSaveInFlightRef.current || isLeadSaving) return false;
    leadSaveInFlightRef.current = true;
    try {
      await persistEarlyLead(contact);
      setEarlyLeadContactSaved(true);
      setEarlyLeadSuccessFlash(true);
      captureEvent(
        ANALYTICS_EVENTS.FUND_FORM_LEAD_CAPTURED,
        buildOpenFundAnalyticsProps(fundType, currentStepRef.current, { lead_id: leadIdRef.current ?? undefined })
      );
      return true;
    } catch {
      return false;
    } finally {
      leadSaveInFlightRef.current = false;
    }
  };

  const handleStepChange = useCallback(
    (stepIndex: number, analyticsStepId?: string) => {
      formStepIndexRef.current = stepIndex;
      if (analyticsStepId) {
        currentStepRef.current = analyticsStepId as OpenFundAnalyticsStepId;
      }
      if (leadIdRef.current && !isEditMode) {
        void persistEarlyLead().catch(() => {
          /* best-effort autosave */
        });
      }
    },
    [isEditMode, persistEarlyLead]
  );

  const validateFields = async (fields: FieldPath<OpenFundFormData>[]) => {
    setFormError(null);
    const ok = await methods.trigger(fields);
    if (!ok) {
      const missingCount = countErrorFields(methods.formState.errors);
      setFormError(missingCount > 0 ? t('openFundRequest.errors.formInvalidWithCount', { count: missingCount }) : t('openFundRequest.errors.formInvalid'));
      scrollToFirstInvalidField();
      const field = firstErrorField(methods.formState.errors) ?? fields[0];
      lastFieldRef.current = field;
      currentStepRef.current = openFundStepFromFieldName(field, isWelfare);
      captureEvent(
        ANALYTICS_EVENTS.FUND_FORM_FIELD_ERROR,
        buildOpenFundAnalyticsProps(fundType, currentStepRef.current, {
          field,
          ...analyticsExtras(),
        })
      );
    }
    return ok;
  };

  const welfareApplicantFields = ['isCoordinator', 'supportedAwareOfFund', 'supportedCanViewFund', 'coordinators'] as const;

  const definitionStep: StepDefinition = {
    analyticsStepId: OPEN_FUND_ANALYTICS_STEPS.FUND_DEFINITION,
    content: <FundDefinitionStep classification={classification} />,
    validate: () =>
      validateFields(
        (isWelfare
          ? ['fundName', 'fundGoals', 'otherGoalDescription', 'reasonSummary']
          : ['fundName', 'responsibleParty', 'responsibleEntityName', 'fundGoals', 'otherGoalDescription', 'reasonSummary']) as FieldPath<OpenFundFormData>[]
      ),
  };

  const termsStep: StepDefinition = {
    analyticsStepId: OPEN_FUND_ANALYTICS_STEPS.TERMS_SIGNATURE,
    content: <TermsAndSignatureStep />,
    validate: () =>
      validateFields([
        'personalInterfaceInNedarimPlus',
        'crowdfundingCampaign',
        'termsAndConditionsAccepted',
        'regulationsAccepted',
        'signature',
      ] as FieldPath<OpenFundFormData>[]),
  };

  const steps: StepDefinition[] = isWelfare
    ? [
        definitionStep,
        {
          analyticsStepId: OPEN_FUND_ANALYTICS_STEPS.SUPPORTEDS,
          content: (
            <div className="flex flex-col items-center w-full gap-0">
              <SupportedsStep />
            </div>
          ),
          validate: () => validateFields(['supporteds'] as FieldPath<OpenFundFormData>[]),
        },
        {
          analyticsStepId: OPEN_FUND_ANALYTICS_STEPS.APPLICANT_COORDINATORS,
          content: <ApplicantAndCoordinatorsStep classification={classification} />,
          validate: () => validateFields([...welfareApplicantFields] as FieldPath<OpenFundFormData>[]),
        },
        termsStep,
      ]
    : [
        definitionStep,
        {
          analyticsStepId: OPEN_FUND_ANALYTICS_STEPS.APPLICANT_COORDINATORS,
          content: <ApplicantAndCoordinatorsStep classification={classification} />,
          validate: () => validateFields(['coordinators'] as FieldPath<OpenFundFormData>[]),
        },
        termsStep,
      ];

  const submitForm = async (data: OpenFundFormData) => {
    setFormError(null);
    try {
      if (isEditMode) {
        await resubmitRequest((await mapFormDataToResubmitDto(data)) as never);
      } else {
        const dto = await mapFormDataToDto(data, privacyAcceptedAt ?? undefined);
        if (leadIdRef.current && leadTokenRef.current) {
          (dto as OpenFundRequestDtoOut & { earlyLeadPublicId?: string; earlyLeadResumeToken?: string }).earlyLeadPublicId = leadIdRef.current;
          (dto as OpenFundRequestDtoOut & { earlyLeadResumeToken?: string }).earlyLeadResumeToken = leadTokenRef.current;
        }
        await submitRequest(dto);
      }
      submittedOkRef.current = true;
      clearStoredOpenFundEarlyLead();
      captureEvent(ANALYTICS_EVENTS.FUND_FORM_SUBMITTED, buildOpenFundAnalyticsProps(fundType, OPEN_FUND_ANALYTICS_STEPS.TERMS_SIGNATURE, analyticsExtras()));
      onSuccess();
      offerTaskSatisfaction('openFund');
    } catch (error) {
      console.error('openFundRequest submit error', error);
      const message =
        error instanceof EmptyFileError || error instanceof FileReadError
          ? translateFileReadError(error, t)
          : extractErrorMessage(error, t('openFundRequest.errors.submitFailed'));
      setFormError(message);
    }
  };

  const handleSubmit = () =>
    methods.handleSubmit(() => {
      setRulesAccepted(false);
      setShowRulesDialog(true);
    })();

  const handleConfirmRules = async () => {
    setShowRulesDialog(false);
    await submitForm(methods.getValues());
  };

  const showEarlyLead = !isEditMode;
  const showEarlyLeadExpandLink = showEarlyLead && !earlyLeadContactSaved && !resumedEarlyLead && !earlyLeadExpanded && !earlyLeadSuccessFlash;
  const showEarlyLeadPanel = showEarlyLead && earlyLeadExpanded && !earlyLeadContactSaved && !resumedEarlyLead;

  return (
    <EditModeContext.Provider value={isEditMode}>
      <ExistingDocumentsContext.Provider value={existingDocuments ?? EMPTY_EXISTING_DOCS}>
        <OpenFundAnalyticsProvider value={trackFieldInteraction}>
          <FormProvider {...methods}>
            <div className={formLayoutStyles.shell}>
              {showEarlyLeadExpandLink && (
                <div className={formLayoutStyles.earlyLeadExpandBar}>
                  <button
                    type="button"
                    className={formLayoutStyles.expandLink}
                    aria-expanded={earlyLeadExpanded}
                    aria-controls={OPEN_FUND_EARLY_LEAD_PANEL_ID}
                    onClick={() => setEarlyLeadExpanded(true)}
                  >
                    {t('openFundRequest.earlyLead.expandPrompt')}
                  </button>
                </div>
              )}
              {showEarlyLead && earlyLeadSuccessFlash && (
                <OpenFundEarlyLeadCapture
                  onSave={handleLeadCapture}
                  isSaving={isLeadSaving}
                  contactSaved={earlyLeadContactSaved}
                  showSuccessFlash={earlyLeadSuccessFlash}
                  onSuccessFlashDismiss={() => {
                    setEarlyLeadSuccessFlash(false);
                    setEarlyLeadExpanded(false);
                  }}
                  startCollapsed={false}
                />
              )}
              <div className={formLayoutStyles.stepperHost}>
                <FormStepper
                  steps={steps}
                  onSubmit={handleSubmit}
                  onStepChange={handleStepChange}
                  initialStepIndex={resumedEarlyLead?.formStepIndex ?? 0}
                  isSubmitting={isPending || isResubmitPending || methods.formState.isSubmitting}
                  errorMessage={formError}
                  scrollHeader={
                    showEarlyLeadPanel ? (
                      <OpenFundEarlyLeadCapture
                        onSave={async (contact) => {
                          const saved = await handleLeadCapture(contact);
                          if (saved) {
                            setEarlyLeadExpanded(false);
                          }
                          return saved;
                        }}
                        isSaving={isLeadSaving}
                        contactSaved={earlyLeadContactSaved}
                        showSuccessFlash={false}
                        onSuccessFlashDismiss={() => setEarlyLeadSuccessFlash(false)}
                        startCollapsed={false}
                        emailInputRef={earlyLeadEmailRef}
                      />
                    ) : undefined
                  }
                />
              </div>
            </div>
          </FormProvider>
        </OpenFundAnalyticsProvider>

        <FundRulesDialog
          open={showRulesDialog}
          setOpen={setShowRulesDialog}
          accepted={rulesAccepted}
          onAcceptedChange={setRulesAccepted}
          onConfirm={handleConfirmRules}
          confirmDisabled={isPending || isResubmitPending}
        />
      </ExistingDocumentsContext.Provider>
    </EditModeContext.Provider>
  );
};

export default OpenFundForm;
